import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import playerPlaceholderURL from '@sorare/core/src/assets/players/placeholder.png';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  LinkBox,
  LinkOther,
  LinkOverlay,
} from '@sorare/core/src/atoms/navigation/Box';
import type { TypographyVariant } from '@sorare/core/src/atoms/typography';
import { HeadlineL } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { ANY_SPORT_SCOUTING_CENTER_WATCHLISTS_ID } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { glossary } from '@sorare/core/src/lib/glossary';
import { ClickMarketHomeSection_Target } from '@sorare/core/src/protos/events/so5/shared/events';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { WatchlistMetas } from '../WatchlistMetas';
import { ManageWatchlist } from './ManageWatchlist';
import type { WatchlistItem_watchlist } from './__generated__/index.graphql';

type Props = {
  watchlist: WatchlistItem_watchlist;
  hideOwner?: boolean;
  myWatchlists?: boolean;
  onRemove?: () => void;
  WatchlistTitle?: TypographyVariant;
  track?: (p: {
    target: ClickMarketHomeSection_Target;
    watchlistSlug?: string;
  }) => void;
};
const Root = styled.div`
  position: relative;
  isolation: isolate;
  --hover-color: var(--c-nd-150);
  transition: color 0.2s ease-in-out;

  &:hover {
    color: var(--c-blue-300);
  }

  &.should-be-removed {
    opacity: 0.5;
  }
`;

const Players = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  grid-template-rows: 3fr 2fr;
`;

const Player = styled.div`
  width: 100%;
  flex-shrink: 0;
  aspect-ratio: 138 / 132;
  border: 1px solid var(--c-nd-150);
  overflow: hidden;
  background: linear-gradient(180deg, var(--c-nd-150) 0%, var(--c-black) 100%);
  transition: background-color 0.2s ease-in-out;
  ${Root}:hover & {
    background: linear-gradient(
      180deg,
      var(--hover-color) 0%,
      var(--c-black) 100%
    );
  }
  &.item-0 {
    grid-area: 1 / 1 / 2 / 4;
    border-top-left-radius: var(--double-unit);
  }
  &.item-1 {
    grid-area: 1 / 4 / 2 / 7;
    border-top-right-radius: var(--double-unit);
  }
  &.item-2 {
    grid-area: 2 / 1 / 3 / 3;
    border-bottom-left-radius: var(--double-unit);
  }
  &.item-3 {
    grid-area: 2 / 3 / 3 / 5;
  }
  &.item-4 {
    grid-area: 2 / 5 / 3 / 7;
    border-bottom-right-radius: var(--double-unit);
  }
`;

const PlayerImage = styled.div`
  transition: 0.3s ease-in-out;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  ${Root}:hover & {
    transform: scale(1.01);
  }
`;

const CornerUpRight = styled(Horizontal)`
  display: none;
  @media ${tabletAndAbove} {
    ${Root}:hover & {
      display: flex;
      position: absolute;
      top: var(--half-unit);
      right: var(--half-unit);
    }
  }
`;

const TopLeft = styled.div`
  position: absolute;
  top: var(--half-unit);
  left: var(--half-unit);
`;

export const WatchlistItem = ({
  watchlist,
  hideOwner,
  myWatchlists,
  WatchlistTitle = HeadlineL,
  onRemove,
  track,
}: Props) => {
  const trackFn = (target: ClickMarketHomeSection_Target) =>
    track?.({
      target,
      watchlistSlug: watchlist.slug,
      ...(watchlist.user && { ownerSlug: watchlist.user.slug }),
    });

  const { generateSportContextPath } = useSportContext();
  const { isMe } = useCurrentUserContext();
  const shouldBeRemoved =
    myWatchlists && !isMe(watchlist.user) && !watchlist.currentUserSubscription;
  const showPublicPrivate = myWatchlists && isMe(watchlist.user);

  const { playersPanel } = watchlist;

  const slots = Array(5)
    .fill(undefined)
    .map((_, k) => k);

  return (
    <Root className={shouldBeRemoved ? 'should-be-removed' : ''}>
      <Vertical gap={1.5} as={LinkBox}>
        <Players>
          {slots.map(k => (
            <Player
              key={playersPanel?.[k]?.id || k}
              className={`item-${k}`}
              {...(playersPanel?.[k]?.anyPlayer?.activeClub
                ?.styleGuideColor && {
                style: {
                  '--hover-color': `${playersPanel?.[k]?.anyPlayer?.activeClub?.styleGuideColor}40`,
                } as CSSProperties,
              })}
            >
              <PlayerImage
                style={{
                  backgroundImage: `url(${playersPanel?.[k]?.anyPlayer.squaredPictureUrl || playerPlaceholderURL})`,
                }}
              />
            </Player>
          ))}
          {!!showPublicPrivate && (
            <TopLeft>
              <Chip color="transparent" size="small">
                <FormattedMessage
                  {...(watchlist.public
                    ? { ...glossary.public }
                    : { ...glossary.private })}
                />
              </Chip>
            </TopLeft>
          )}
        </Players>
        <Vertical>
          <WatchlistTitle>
            <Ellipsis>
              <LinkOverlay
                as={Link}
                to={generateSportContextPath(
                  ANY_SPORT_SCOUTING_CENTER_WATCHLISTS_ID,
                  {
                    params: { id: idFromObject(watchlist.id) },
                  }
                )}
                onClick={() => trackFn(ClickMarketHomeSection_Target.WATCHLIST)}
              >
                {watchlist.title}
              </LinkOverlay>
            </Ellipsis>
          </WatchlistTitle>
          <LinkOther as="div">
            <WatchlistMetas
              watchlist={watchlist}
              hideOwner={hideOwner}
              track={() => trackFn(ClickMarketHomeSection_Target.USER_GALLERY)}
            />
          </LinkOther>
        </Vertical>
      </Vertical>
      <CornerUpRight>
        <LinkOther as="div">
          <ManageWatchlist watchlist={watchlist} onRemove={onRemove} />
        </LinkOther>
      </CornerUpRight>
    </Root>
  );
};

WatchlistItem.fragments = {
  watchlist: gql`
    fragment WatchlistItem_watchlist on Watchlist {
      slug
      id
      title
      user {
        slug
      }
      currentUserSubscription {
        id
        slug
      }
      playersPanel {
        id
        anyPlayer {
          slug
          squaredPictureUrl
          activeClub {
            slug
            styleGuideColor
          }
        }
      }
      ...WatchlistMetas_watchlist
      ...ManageWatchlist_watchlist
    }
    ${WatchlistMetas.fragments.watchlist}
    ${ManageWatchlist.fragments.watchlist}
  ` as TypedDocumentNode<WatchlistItem_watchlist>,
};
